import { PresentCanvasIcon } from '@carboncopyco/icons';
import { type IGlobalTab, TabGroupName } from '@carboncopyco/types/desktop';
import {
  ToggleButton,
  Tooltip,
  TooltipBubble,
  TooltipMessage,
} from '@carboncopyco/ui';
import { useEffect, useRef } from 'react';
import { useTabManager } from '../../services/TabManager';
import { useSpeakerPanelContext } from '../../store/context';

import {
  SettingMainItemsKeys,
  SettingsSubKeys,
} from '@carboncopyco/types/desktop/appSettings';
import { settingsState } from '../../../shared/appSettings';
import { isActiveOrAllInnerTabsLoaded } from '../../shared/utils';

const SpeakerPanelButton = ({
  tab,
  isButtonDisabled = false,
}: {
  tab: IGlobalTab;
  isButtonDisabled?: boolean;
}) => {
  const {
    isSpeakerPanelOpen,
    setIsSpeakerPanelOpen,
    isSpeakerPanelWarningVisible,
    setIsSpeakerPanelWarningVisible,
  } = useSpeakerPanelContext();
  const { getActiveGlobalTab } = useTabManager();
  const tooltipAnchorRef = useRef(null);
  const activeTab = getActiveGlobalTab();
  const isActiveTab = activeTab?.id === tab?.id;
  const tabHasSpeakerPanel = tab.group;
  const isInnerViewLoaded = isActiveOrAllInnerTabsLoaded(tab);

  const isWarningVisible =
    isSpeakerPanelWarningVisible &&
    tab.group?.name === TabGroupName.PlaybookPreview;

  const toggleSpeakerPanel = async () => {
    setIsSpeakerPanelWarningVisible(false);
    setIsSpeakerPanelOpen(!isSpeakerPanelOpen);

    if (tabHasSpeakerPanel && tab.group?.id) {
      if (isSpeakerPanelOpen) {
        await window.dsDesktop.speakerPanel.closeFromSpeakerPanelIfOpen({
          resetData: true,
        });
      }
      if (!isSpeakerPanelOpen) await window.dsDesktop.speakerPanel.open();
    }
  };

  const initSpeakerPanelState = async () => {
    const isSpeakerPanelTab = tab.group;
    if (isSpeakerPanelTab) return;

    const isSpeakerPanelEnabledInSettings = await settingsState.getItem(
      SettingMainItemsKeys.PresentationDisplay,
      SettingsSubKeys.ShowSpeakerPanelByDefault
    );
    setIsSpeakerPanelOpen(isSpeakerPanelEnabledInSettings);
  };

  useEffect(() => {
    initSpeakerPanelState();
  }, []);

  return (
    <div
      className="flex align-center"
      id="speakerPanel"
    >
      <div ref={tooltipAnchorRef}>
        <TooltipBubble
          title="Speaker panel"
          placement="left"
        >
          <ToggleButton
            disabled={
              !tabHasSpeakerPanel ||
              !isActiveTab ||
              !isInnerViewLoaded ||
              isButtonDisabled
            }
            dark
            selected={isSpeakerPanelOpen}
            icon={<PresentCanvasIcon size={20} />}
            onSelect={toggleSpeakerPanel}
            noDrag={window?.dsDesktop?.app?.isWindows}
            warning={isWarningVisible}
          />
        </TooltipBubble>
      </div>

      {isWarningVisible && (
        <Tooltip
          anchorRef={tooltipAnchorRef}
          placement="top-start"
          noArrow
          appendTo={() => document.body}
        >
          <TooltipMessage message="Use the speaker panel to toggle between your demos. Only you can see this." />
        </Tooltip>
      )}
    </div>
  );
};

export default SpeakerPanelButton;
